import React, { useState, useEffect } from "react";
import Sidebar from "./Sidebar";
import axios from "axios";
import Swal from "sweetalert2";
import "./dashboard.css";
import "./addTeacher.css";

const API_BASE_URL = "https://mhsdev.online/MHS-WEB/api";

const AddTeacherForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    fatherName: "",
    cnic: "",
    primaryMobile: "",
    gmail: "",
    gender: "",
    address: "",
    highestEducation: "",
    gradeOrCgpa: "",
    selectFor: "",
    inchargeOf: "",
    teacherId: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [charCount, setCharCount] = useState(0);
  const [isGeneratingId, setIsGeneratingId] = useState(false);
  const [backendStatus, setBackendStatus] = useState({
    connected: false,
    checking: true,
  });

  const educationLevels = [
    "Matric",
    "Intermediate",
    "Bachelor",
    "Master",
    "BS-16 Years",
    "M-Phil",
    "PhD",
  ];

  const subjectsFor = ["Science Subjects", "Arts Subjects", "Hybrid"];

  const classes = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  const genderOptions = ["Male", "Female", "Other"];

  useEffect(() => {
    checkBackendConnection();
  }, []);

  const checkBackendConnection = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/TeacherCrud.php`, {
        params: { action: "get-last-teacher-id" },
      });
      setBackendStatus({ connected: true, checking: false });
    } catch (error) {
      setBackendStatus({ connected: false, checking: false });
      showWarningModal(
        "Connection Issue",
        "Unable to connect to the server",
        "Please check if the PHP backend is running",
        false
      );
    }
  };

  const formatCNIC = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 5) return numbers;
    if (numbers.length <= 12)
      return `${numbers.slice(0, 5)}-${numbers.slice(5)}`;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 12)}-${numbers.slice(
      12,
      13
    )}`;
  };

  const formatMobile = (value) => {
    const numbers = value.replace(/\D/g, "");
    if (numbers.length <= 4) return numbers;
    return `${numbers.slice(0, 4)}-${numbers.slice(4, 11)}`;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Teacher name is required";
    if (!formData.fatherName.trim())
      newErrors.fatherName = "Father's name is required";

    const cnicRegex = /^\d{5}-\d{7}-\d{1}$/;
    if (!formData.cnic.match(cnicRegex)) {
      newErrors.cnic = "CNIC must be in format: 35202-1234567-1";
    }

    const mobileRegex = /^03\d{2}-\d{7}$/;
    if (!formData.primaryMobile.match(mobileRegex)) {
      newErrors.primaryMobile = "Mobile must be in format: 0300-1234567";
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@gmail\.com$/;
    if (!formData.gmail || !formData.gmail.match(emailRegex)) {
      newErrors.gmail =
        "Valid Gmail address is required (must end with @gmail.com)";
    }

    if (!formData.gender) {
      newErrors.gender = "Gender is required";
    }

    if (!formData.address.trim()) newErrors.address = "Address is required";
    if (!formData.highestEducation)
      newErrors.highestEducation = "Highest education is required";
    if (!formData.selectFor)
      newErrors.selectFor = "Subject specialization is required";
    if (!formData.teacherId)
      newErrors.teacherId = "Teacher ID is required. Please generate it first.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    let formattedValue = value;

    if (name === "cnic") {
      formattedValue = formatCNIC(value);
    } else if (name === "primaryMobile") {
      formattedValue = formatMobile(value);
    }

    if (name === "address") {
      setCharCount(formattedValue.length);
    }

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));
  };

  const showWarningModal = (title, message, details = "", showRetry = false) => {
    Swal.fire({
      title: title,
      html: `
        <div style="text-align: left; padding: 15px;">
          <div style="background: #fff8e1; padding: 12px; border-radius: 8px; border-left: 4px solid #ffa000; margin-bottom: 15px;">
            <p style="color: #5d4037; margin: 0; font-weight: 500; font-size: 16px;">
              <span style="display: inline-block; margin-right: 10px;">⚠️</span>
              ${message}
            </p>
            ${details ? `
              <div style="margin-top: 10px; padding: 10px; background: white; border-radius: 4px; border: 1px solid #ffcc80;">
                <p style="color: #666; font-size: 14px; margin: 0;"><strong>Details:</strong> ${details}</p>
              </div>
            ` : ''}
          </div>
          <div style="font-size: 14px; color: #666; background: #f5f5f5; padding: 12px; border-radius: 6px;">
            <p style="margin: 0 0 8px 0; font-weight: 500;">Suggestions:</p>
            <ul style="margin: 0; padding-left: 20px;">
              <li>Check if all information is correct</li>
              <li>Verify that the teacher doesn't already exist</li>
              <li>Try again in a few moments</li>
            </ul>
          </div>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: showRetry ? "Try Again" : "OK",
      confirmButtonColor: "#ff9800",
      showCancelButton: showRetry,
      cancelButtonText: "Cancel",
      cancelButtonColor: "#9e9e9e",
      width: 500,
      customClass: {
        popup: 'swal2-warning-modal'
      }
    }).then((result) => {
      if (result.isConfirmed && showRetry) {
        handleSubmit({ preventDefault: () => {} });
      }
    });
  };

  const showSuccessModal = (title, message, teacherId, teacherName) => {
    Swal.fire({
      title: title,
      html: `
        <div style="text-align: center; padding: 20px;">
          <div style="font-size: 60px; color: #4caf50; margin: 20px 0;">✓</div>
          <h3 style="color: #2e7d32; margin-bottom: 20px;">${message}</h3>
          <div style="background: #e8f5e9; padding: 20px; border-radius: 10px; margin: 20px 0; border: 2px solid #a5d6a7;">
            <div style="font-size: 28px; font-weight: 700; color: #1b5e20; margin-bottom: 10px;">
              ${teacherId}
            </div>
            <p style="color: #388e3c; font-size: 18px; margin: 0;">
              ${teacherName}
            </p>
          </div>
          <p style="color: #666; font-size: 16px;">
            Teacher has been successfully registered in the system.
          </p>
        </div>
      `,
      showConfirmButton: true,
      confirmButtonText: "Register Another",
      confirmButtonColor: "#4caf50",
      width: 500,
      customClass: {
        popup: 'swal2-success-modal'
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showWarningModal(
        "Validation Required",
        "Please fix the following issues before submitting:",
        Object.values(errors).filter(err => err).join(", "),
        false
      );
      return;
    }

    setIsLoading(true);

    try {
      const postData = new URLSearchParams();
      postData.append("action", "add-teacher");
      postData.append("name", formData.name);
      postData.append("fatherName", formData.fatherName);
      postData.append("cnic", formData.cnic);
      postData.append("primaryMobile", formData.primaryMobile);
      postData.append("gmail", formData.gmail);
      postData.append("gender", formData.gender);
      postData.append("address", formData.address);
      postData.append("highestEducation", formData.highestEducation);
      postData.append("gradeOrCgpa", formData.gradeOrCgpa || "");
      postData.append("selectFor", formData.selectFor);
      postData.append("inchargeOf", formData.inchargeOf || "");
      postData.append("teacherId", formData.teacherId);

      const response = await axios.post(
        `${API_BASE_URL}/TeacherCrud.php`,
        postData.toString(),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          timeout: 15000,
        }
      );

      if (response.data.success) {
        showSuccessModal(
          "Success!",
          "Teacher Registered Successfully",
          formData.teacherId,
          formData.name
        );

        // Reset form
        setFormData({
          name: "",
          fatherName: "",
          cnic: "",
          primaryMobile: "",
          gmail: "",
          gender: "",
          address: "",
          highestEducation: "",
          gradeOrCgpa: "",
          selectFor: "",
          inchargeOf: "",
          teacherId: "",
        });
        setCharCount(0);
        setErrors({});
      } else {
        showWarningModal(
          "Registration Issue",
          response.data.error || "Could not register teacher",
          response.data.details || "",
          false
        );
      }
    } catch (error) {
      console.error("Registration error:", error);

      let userMessage = "We couldn't register the teacher at this time.";
      let technicalMessage = "";
      let showRetry = false;

      if (error.response) {
        if (error.response.status === 400) {
          userMessage = "Please check your information and try again.";
          technicalMessage = error.response.data.error || "Invalid form data";
        } else if (error.response.status === 409) {
          userMessage = "This teacher appears to already exist in our system.";
          technicalMessage = error.response.data.details || "Duplicate entry found";
          showRetry = false;
        } else if (error.response.status === 500) {
          userMessage = "Our system is experiencing technical difficulties.";
          technicalMessage = "Server error. Please try again later.";
          showRetry = true;
        } else {
          userMessage = "An unexpected error occurred.";
          technicalMessage = error.response.data?.error || "Unknown server error";
        }
      } else if (error.code === 'ECONNABORTED') {
        userMessage = "The request took too long. Please check your connection.";
        technicalMessage = "Request timeout";
        showRetry = true;
      } else if (error.message.includes("Network Error")) {
        userMessage = "Cannot connect to the server. Please check your internet connection.";
        technicalMessage = "Network connection failed";
        showRetry = true;
      }

      showWarningModal("Registration Issue", userMessage, technicalMessage, showRetry);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateTeacherID = async () => {
    if (!formData.name || !formData.fatherName) {
      showWarningModal(
        "Information Required",
        "Please fill in teacher name and father's name first.",
        "",
        false
      );
      return;
    }

    setIsGeneratingId(true);

    try {
      const response = await axios.get(`${API_BASE_URL}/TeacherCrud.php`, {
        params: {
          action: "get-last-teacher-id",
        },
      });

      const lastId = response.data.lastId || "";
      let newIdNumber;

      if (lastId) {
        const matches = lastId.match(/[A-Za-z]+-(\d+)/);
        if (matches) {
          const lastNum = parseInt(matches[1], 10);
          newIdNumber = lastNum + 1;
        } else {
          newIdNumber = 1;
        }
      } else {
        newIdNumber = 1;
      }

      const newTeacherId = `Prof-${newIdNumber.toString().padStart(4, "0")}`;

      setFormData((prev) => ({ ...prev, teacherId: newTeacherId }));

      Swal.fire({
        icon: "success",
        title: "Teacher ID Generated",
        html: `
        <div style="text-align: center; padding: 20px;">
          <div style="background: #e3f2fd; padding: 20px; border-radius: 10px; margin-bottom: 20px; border: 2px solid #bbdefb;">
            <div style="font-size: 32px; font-weight: 700; color: #1565c0; margin-bottom: 10px;">
              ${newTeacherId}
            </div>
            <p style="color: #1976d2; font-size: 16px; margin: 0;">Ready to use</p>
          </div>
          ${lastId ? `<p style="color: #666;">Previous ID: <strong>${lastId}</strong></p>` : ''}
          <p style="color: #666; font-size: 14px;">Format: <strong>Prof-{Sequence}</strong></p>
        </div>
      `,
        showConfirmButton: true,
        confirmButtonText: "Use This ID",
        confirmButtonColor: "#2196f3",
        width: 450,
        customClass: {
          popup: 'swal2-info-modal'
        }
      });
    } catch (error) {
      const yearPart = new Date().getFullYear().toString().slice(-2);
      const timestamp = Date.now().toString().slice(-4);
      const newTeacherId = `Prof-${yearPart}${timestamp.slice(0, 2)}`;

      setFormData((prev) => ({ ...prev, teacherId: newTeacherId }));

      showWarningModal(
        "Generated ID (Fallback Mode)",
        `Generated ID: ${newTeacherId}`,
        "Using fallback mode. Check backend connection.",
        false
      );
    } finally {
      setIsGeneratingId(false);
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: "Clear Form?",
      html: `
        <div style="text-align: center; padding: 10px;">
          <div style="font-size: 48px; color: #ff9800; margin: 20px 0;">⚠️</div>
          <p style="color: #5d4037; font-size: 16px; margin-bottom: 20px;">
            All entered data will be permanently cleared.
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Yes, Clear Form",
      cancelButtonText: "No, Keep Data",
      confirmButtonColor: "#ff9800",
      cancelButtonColor: "#9e9e9e",
      width: 400
    }).then((result) => {
      if (result.isConfirmed) {
        setFormData({
          name: "",
          fatherName: "",
          cnic: "",
          primaryMobile: "",
          gmail: "",
          gender: "",
          address: "",
          highestEducation: "",
          gradeOrCgpa: "",
          selectFor: "",
          inchargeOf: "",
          teacherId: "",
        });
        setErrors({});
        setCharCount(0);
        
        Swal.fire({
          icon: "success",
          title: "Form Cleared",
          text: "All form data has been reset.",
          showConfirmButton: false,
          timer: 1500
        });
      }
    });
  };

  return (
    <div className="teacher-dashboard-wrapper">
      {isLoading && (
        <div className="teacher-loading-overlay">
          <div className="teacher-loader"></div>
          <p style={{ marginTop: "1rem", color: "#4a5568" }}>Processing...</p>
        </div>
      )}

      <Sidebar />

      <div className="teacher-main-content">
        <div className="teacher-page-header">
          <h1 className="teacher-page-title">Register New Teacher</h1>
          <p className="teacher-page-subtitle">
            Fill in all required fields to add a new teacher to the system. All
            fields marked with <span style={{ color: "#e53e3e" }}>*</span> are
            required.
          </p>
        </div>

        {!backendStatus.connected && !backendStatus.checking && (
          <div className="teacher-backend-warning">
            <div className="warning-icon">⚠️</div>
            <div className="warning-content">
              <h4>Backend Connection Issue</h4>
              <p>
                Unable to connect to PHP backend. Some features may not work.
                Please check:
              </p>
              <ul>
                <li>Is the PHP server running on localhost?</li>
                <li>
                  Is the file accessible at:{" "}
                  <code>{API_BASE_URL}/TeacherCrud.php</code>?
                </li>
                <li>Check browser console for detailed errors.</li>
              </ul>
            </div>
          </div>
        )}

        <form className="teacher-pro-form" onSubmit={handleSubmit}>
          {/* Personal Information Card */}
          <div className="teacher-form-card" data-step="1">
            <div className="teacher-card-header">
              <div className="teacher-card-header-left">
                <h3>
                  <span className="teacher-step-number">1</span>
                  Personal Information
                </h3>
              </div>
              <div className="teacher-card-header-right">
                <span className="teacher-card-badge">Required</span>
              </div>
            </div>
            <div className="teacher-form-grid">
              <div className="teacher-input-group">
                <label className="teacher-required-field">
                  <span className="teacher-field-icon">👤</span>
                  Full Name
                </label>
                <input
                  className={`teacher-form-input ${
                    errors.name
                      ? "teacher-is-invalid"
                      : formData.name
                      ? "teacher-is-valid"
                      : ""
                  }`}
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter full name"
                  maxLength="100"
                  disabled={isLoading}
                />
                {errors.name && (
                  <span className="teacher-error-message">{errors.name}</span>
                )}
              </div>

              <div className="teacher-input-group">
                <label className="teacher-required-field">
                  <span className="teacher-field-icon">👨</span>
                  Father's Name
                </label>
                <input
                  className={`teacher-form-input ${
                    errors.fatherName
                      ? "teacher-is-invalid"
                      : formData.fatherName
                      ? "teacher-is-valid"
                      : ""
                  }`}
                  type="text"
                  name="fatherName"
                  value={formData.fatherName}
                  onChange={handleInputChange}
                  placeholder="Enter father's full name"
                  maxLength="100"
                  disabled={isLoading}
                />
                {errors.fatherName && (
                  <span className="teacher-error-message">
                    {errors.fatherName}
                  </span>
                )}
              </div>

              <div className="teacher-input-group">
                <label className="teacher-required-field">
                  <span className="teacher-field-icon">🆔</span>
                  CNIC Number
                </label>
                <div className="teacher-input-with-prefix">
                  <span className="teacher-input-prefix">CNIC</span>
                  <input
                    className={`teacher-form-input ${
                      errors.cnic
                        ? "teacher-is-invalid"
                        : formData.cnic
                        ? "teacher-is-valid"
                        : ""
                    }`}
                    type="text"
                    name="cnic"
                    value={formData.cnic}
                    onChange={handleInputChange}
                    placeholder="35202-1234567-1"
                    maxLength="15"
                    disabled={isLoading}
                  />
                </div>
                <div className="teacher-form-hint">
                  <span className="hint-icon">💡</span>
                  Format: 35202-1234567-1 (Auto-formats as you type)
                </div>
                {errors.cnic && (
                  <span className="teacher-error-message">{errors.cnic}</span>
                )}
              </div>

              <div className="teacher-input-group">
                <label className="teacher-required-field">
                  <span className="teacher-field-icon">📱</span>
                  Primary Mobile
                </label>
                <div className="teacher-input-with-prefix">
                  <span className="teacher-input-prefix">+92</span>
                  <input
                    className={`teacher-form-input ${
                      errors.primaryMobile
                        ? "teacher-is-invalid"
                        : formData.primaryMobile
                        ? "teacher-is-valid"
                        : ""
                    }`}
                    type="text"
                    name="primaryMobile"
                    value={formData.primaryMobile}
                    onChange={handleInputChange}
                    placeholder="0300-1234567"
                    maxLength="12"
                    disabled={isLoading}
                  />
                </div>
                <div className="teacher-form-hint">
                  <span className="hint-icon">💡</span>
                  Format: 0300-1234567 (Auto-formats as you type)
                </div>
                {errors.primaryMobile && (
                  <span className="teacher-error-message">
                    {errors.primaryMobile}
                  </span>
                )}
              </div>

              <div className="teacher-input-group">
                <label className="teacher-required-field">
                  <span className="teacher-field-icon">📧</span>
                  Gmail Address
                </label>
                <div className="teacher-input-with-prefix">
                  <span className="teacher-input-prefix">@</span>
                  <input
                    className={`teacher-form-input ${
                      errors.gmail
                        ? "teacher-is-invalid"
                        : formData.gmail
                        ? "teacher-is-valid"
                        : ""
                    }`}
                    type="email"
                    name="gmail"
                    value={formData.gmail}
                    onChange={handleInputChange}
                    placeholder="username@gmail.com"
                    disabled={isLoading}
                  />
                </div>
                <div className="teacher-form-hint">
                  <span className="hint-icon">💡</span>
                  Must be a valid Gmail address (ends with @gmail.com)
                </div>
                {errors.gmail && (
                  <span className="teacher-error-message">{errors.gmail}</span>
                )}
              </div>

              <div className="teacher-input-group">
                <label className="teacher-required-field">
                  <span className="teacher-field-icon">⚤</span>
                  Gender
                </label>
                <div className="teacher-select-wrapper">
                  <select
                    className={`teacher-form-select ${
                      errors.gender
                        ? "teacher-is-invalid"
                        : formData.gender
                        ? "teacher-is-valid"
                        : ""
                    }`}
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  >
                    <option value="">Select Gender</option>
                    {genderOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <span className="teacher-select-arrow">▼</span>
                </div>
                {errors.gender && (
                  <span className="teacher-error-message">{errors.gender}</span>
                )}
              </div>

              <div className="teacher-input-group full-width">
                <label className="teacher-required-field">
                  <span className="teacher-field-icon">🏠</span>
                  Residential Address
                </label>
                <div className="teacher-textarea-wrapper">
                  <textarea
                    className={`teacher-form-textarea ${
                      errors.address
                        ? "teacher-is-invalid"
                        : formData.address
                        ? "teacher-is-valid"
                        : ""
                    }`}
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter complete residential address with city..."
                    rows="4"
                    maxLength="500"
                    disabled={isLoading}
                  />
                  <div className="teacher-textarea-footer">
                    <div
                      className={`teacher-char-counter ${
                        charCount > 450 ? "warning" : ""
                      }`}
                    >
                      {charCount}/500 characters
                    </div>
                    <div className="teacher-address-hint">
                      Include house number, street, city, and postal code
                    </div>
                  </div>
                </div>
                {errors.address && (
                  <span className="teacher-error-message">
                    {errors.address}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Professional Information Card */}
          <div className="teacher-form-card" data-step="2">
            <div className="teacher-card-header">
              <div className="teacher-card-header-left">
                <h3>
                  <span className="teacher-step-number">2</span>
                  Professional Information
                </h3>
              </div>
              <div className="teacher-card-header-right">
                <span className="teacher-card-badge">Required</span>
              </div>
            </div>
            <div className="teacher-form-grid">
              <div className="teacher-input-group">
                <label className="teacher-required-field">
                  <span className="teacher-field-icon">🎓</span>
                  Highest Education
                </label>
                <div className="teacher-select-wrapper">
                  <select
                    className={`teacher-form-select ${
                      errors.highestEducation
                        ? "teacher-is-invalid"
                        : formData.highestEducation
                        ? "teacher-is-valid"
                        : ""
                    }`}
                    name="highestEducation"
                    value={formData.highestEducation}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  >
                    <option value="">Select Education Level</option>
                    {educationLevels.map((level, index) => (
                      <option key={index} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                  <span className="teacher-select-arrow">▼</span>
                </div>
                {errors.highestEducation && (
                  <span className="teacher-error-message">
                    {errors.highestEducation}
                  </span>
                )}
              </div>

              <div className="teacher-input-group">
                <label>
                  <span className="teacher-field-icon">⭐</span>
                  Grade / CGPA (Optional)
                </label>
                <input
                  className="teacher-form-input"
                  type="text"
                  name="gradeOrCgpa"
                  value={formData.gradeOrCgpa}
                  onChange={handleInputChange}
                  placeholder="e.g., 3.8/4.0, A+, First Division"
                  disabled={isLoading}
                />
              </div>

              <div className="teacher-input-group">
                <label className="teacher-required-field">
                  <span className="teacher-field-icon">📚</span>
                  Subject Category
                </label>
                <div className="teacher-select-wrapper">
                  <select
                    className={`teacher-form-select ${
                      errors.selectFor
                        ? "teacher-is-invalid"
                        : formData.selectFor
                        ? "teacher-is-valid"
                        : ""
                    }`}
                    name="selectFor"
                    value={formData.selectFor}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  >
                    <option value="">Select Category</option>
                    {subjectsFor.map((subject, index) => (
                      <option key={index} value={subject}>
                        {subject}
                      </option>
                    ))}
                  </select>
                  <span className="teacher-select-arrow">▼</span>
                </div>
                {errors.selectFor && (
                  <span className="teacher-error-message">
                    {errors.selectFor}
                  </span>
                )}
              </div>

              <div className="teacher-input-group">
                <label>
                  <span className="teacher-field-icon">👨‍🏫</span>
                  Class In-charge (Optional)
                </label>
                <div className="teacher-select-wrapper">
                  <select
                    className="teacher-form-select"
                    name="inchargeOf"
                    value={formData.inchargeOf}
                    onChange={handleInputChange}
                    disabled={isLoading}
                  >
                    <option value="">Select Class (Optional)</option>
                    {classes.map((cls, index) => (
                      <option key={index} value={cls}>
                        {cls}
                      </option>
                    ))}
                  </select>
                  <span className="teacher-select-arrow">▼</span>
                </div>
              </div>
            </div>
          </div>

          {/* ID Generation Card */}
          <div className="teacher-form-card" data-step="3">
            <div className="teacher-card-header">
              <div className="teacher-card-header-left">
                <h3>
                  <span className="teacher-step-number">3</span>
                  Teacher ID Generation
                </h3>
              </div>
              <div className="teacher-card-header-right">
                <span className="teacher-card-badge success">
                  Auto-generated
                </span>
              </div>
            </div>
            <div className="teacher-id-control-panel">
              <div className="teacher-id-generator">
                <div className="teacher-generator-left">
                  <h4>Generate Unique Teacher ID</h4>
                  <p className="teacher-generator-description">
                    Click the button below to generate a unique teacher ID based
                    on the last ID in the database. The ID format is:{" "}
                    <strong>Prof-XXXX</strong>
                  </p>
                  <div className="teacher-generator-requirements">
                    <span className="requirement-icon">✅</span>
                    <span>Teacher Name is required</span>
                  </div>
                  <div className="teacher-generator-requirements">
                    <span className="requirement-icon">✅</span>
                    <span>Father's Name is required</span>
                  </div>
                </div>
                <div className="teacher-generator-right">
                  <button
                    type="button"
                    className={`teacher-btn-generate ${
                      !formData.name || !formData.fatherName || isLoading
                        ? "disabled"
                        : ""
                    }`}
                    onClick={handleGenerateTeacherID}
                    disabled={
                      !formData.name ||
                      !formData.fatherName ||
                      isGeneratingId ||
                      isLoading
                    }
                  >
                    {isGeneratingId ? (
                      <>
                        <span className="teacher-loader-spinner small"></span>
                        Generating...
                      </>
                    ) : (
                      <>
                        <span className="generate-icon">🎯</span>
                        Generate Teacher ID
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="teacher-id-display-container">
                <div className="teacher-id-display-header">
                  <span className="teacher-field-icon">🆔</span>
                  <span>Generated Teacher ID</span>
                </div>
                <div className="teacher-id-input-wrapper">
                  <div className="teacher-id-prefix">Prof-</div>
                  <input
                    className={`teacher-form-input teacher-id-input ${
                      errors.teacherId
                        ? "teacher-is-invalid"
                        : formData.teacherId
                        ? "teacher-is-valid"
                        : ""
                    }`}
                    type="text"
                    name="teacherId"
                    value={formData.teacherId || ""}
                    onChange={handleInputChange}
                    placeholder="ID will appear here after generation"
                    disabled={isLoading}
                  />
                </div>
                {errors.teacherId && (
                  <span className="teacher-error-message">
                    {errors.teacherId}
                  </span>
                )}
                <div className="teacher-id-hint">
                  <span className="hint-icon">💡</span>
                  This ID will be used for all future references to this teacher
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="teacher-form-actions">
            <div className="teacher-actions-left">
              <button
                type="button"
                className="teacher-btn-clear"
                onClick={handleReset}
                disabled={isLoading}
              >
                <span className="btn-icon">🗑️</span>
                Clear Form
              </button>
              <div className="teacher-form-tips">
                <span className="tip-icon">💡</span>
                <span>All fields will be cleared permanently</span>
              </div>
            </div>
            <div className="teacher-actions-right">
              <button
                type="submit"
                className="teacher-btn-submit"
                disabled={isLoading || isGeneratingId || !formData.teacherId}
              >
                {isLoading ? (
                  <>
                    <span className="teacher-loader-spinner small"></span>
                    Registering...
                  </>
                ) : (
                  <>
                    <span className="btn-icon">📝</span>
                    Register Teacher
                  </>
                )}
              </button>
              <div className="teacher-submit-requirements">
                {!formData.teacherId && (
                  <span className="requirement-warning">
                    ⚠️ Generate Teacher ID first
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Teacher Preview */}
          {formData.name && (
            <div className="teacher-summary">
              <div className="teacher-summary-header">
                <h4>
                  <span className="summary-icon">👤</span>
                  Teacher Preview
                </h4>
                <span className="teacher-summary-status">
                  {formData.teacherId
                    ? "✅ Ready for Registration"
                    : "⏳ Pending ID Generation"}
                </span>
              </div>
              <div className="teacher-summary-grid">
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">Full Name:</span>
                  <span className="teacher-summary-value">{formData.name}</span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">Father's Name:</span>
                  <span className="teacher-summary-value">
                    {formData.fatherName}
                  </span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">CNIC:</span>
                  <span className="teacher-summary-value">
                    {formData.cnic || "Not Provided"}
                  </span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">Primary Mobile:</span>
                  <span className="teacher-summary-value">
                    {formData.primaryMobile || "Not Provided"}
                  </span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">Gmail:</span>
                  <span className="teacher-summary-value">
                    {formData.gmail || "Not Provided"}
                  </span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">Gender:</span>
                  <span className="teacher-summary-value">
                    {formData.gender || "Not Selected"}
                  </span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">
                    Highest Education:
                  </span>
                  <span className="teacher-summary-value">
                    {formData.highestEducation || "Not Selected"}
                  </span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">
                    Subject Category:
                  </span>
                  <span className="teacher-summary-value">
                    {formData.selectFor || "Not Selected"}
                  </span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">
                    Class In-charge:
                  </span>
                  <span className="teacher-summary-value">
                    {formData.inchargeOf || "Not Assigned"}
                  </span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">Teacher ID:</span>
                  <span
                    className={`teacher-summary-value ${
                      formData.teacherId ? "teacher-id-highlight" : ""
                    }`}
                  >
                    {formData.teacherId || "Not Generated"}
                  </span>
                </div>
                <div className="teacher-summary-item">
                  <span className="teacher-summary-label">Address:</span>
                  <span className="teacher-summary-value address-truncate">
                    {formData.address || "Not Provided"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Required Field Note */}
          <div className="teacher-required-note">
            <span className="required-note-icon">📋</span>
            <span>
              Note: All fields marked with an asterisk (*) are required. The
              form will only submit when all validations pass.
            </span>
          </div>
        </form>

        {/* Loading Bar */}
        {isLoading && <div className="teacher-loading-bar"></div>}
      </div>
    </div>
  );
};

export default AddTeacherForm;